import { useCallback, useMemo, useRef, useState } from "react";
import { useProgressStore } from "@/application/stores/progressStore";
import { useSkillMemoryStore } from "@/application/stores/skillMemoryStore";
import { deriveResumeView, type ResumeView } from "@/application/usecases/startLevel";
import type { Drill } from "@/domain/learning/model/drill";
import type { Level } from "@/domain/learning/model/level";
import {
  type PracticePhase,
  presentDrillForPhase,
  scheduleDrillsForRetention,
  skillKeyOf,
} from "@/domain/learning/retention-schedule";
import { withSkillPractice } from "@/domain/learning/skill-memory";
import type { SkillMemoryRepository } from "@/domain/learning/skill-memory-repository";
import type { Progress } from "@/domain/progress/model/progress";
import { withClearedDrill, withStamp } from "@/domain/progress/model/progress";
import type { ProgressRepository } from "@/domain/progress/progress-repository";
import { createProgressRepository } from "@/infrastructure/persistence/progress-repository-factory";
import { createSkillMemoryRepository } from "@/infrastructure/persistence/skill-memory-repository-factory";

/**
 * 段（S-2）の中で切り替わるビュー。**遷移は一方向**であり、ビュー間を戻る操作は作らない
 * （screen-flow.md §6.1・N-003）。このため履歴（`pushState`）にも一切積まない。
 */
export type LevelView =
  | { readonly kind: "intro" }
  | {
      readonly kind: "drill";
      readonly lessonId: string;
      readonly drillId: string;
      readonly drill: Drill;
      readonly phase: PracticePhase;
    }
  | {
      readonly kind: "exam";
      /** 昇段試験の何問目か（0始まり）。朱印は最終問に合格したときだけ授かる。 */
      readonly index: number;
      readonly drill: Drill;
      readonly total: number;
    }
  | { readonly kind: "stamp" }
  | { readonly kind: "outro" };

interface FlatDrillEntry {
  readonly lessonId: string;
  readonly drillId: string;
  readonly drill: Drill;
  readonly phase: PracticePhase;
}

/**
 * レッスン単位で記憶定着スケジューラを通し、フラットな出題列にする（F-011）。
 * レッスン境界は越えない —— 段の物語上の区切りを壊さないため。
 */
function buildFlatDrillList(level: Level): readonly FlatDrillEntry[] {
  return level.lessons.flatMap((lesson) =>
    scheduleDrillsForRetention(lesson.drills).map((scheduled) => ({
      lessonId: lesson.id,
      drillId: scheduled.drill.id,
      drill: presentDrillForPhase(scheduled.drill, scheduled.phase),
      phase: scheduled.phase,
    })),
  );
}

/** 昇段試験のn問目のビュー。教材が空でないことはZodスキーマが保証している。 */
function examView(level: Level, index: number): LevelView {
  const drill = level.exam.drills[index];
  if (!drill) {
    return { kind: "stamp" };
  }
  return { kind: "exam", index, drill, total: level.exam.drills.length };
}

function toInitialView(
  level: Level,
  resume: ResumeView,
  flatDrills: readonly FlatDrillEntry[],
): LevelView {
  if (resume.kind === "intro" || resume.kind === "review-intro") {
    return { kind: "intro" };
  }
  if (resume.kind === "exam") {
    return examView(level, 0);
  }
  const entry = flatDrills.find((candidate) => candidate.drillId === resume.drillId);
  return entry
    ? {
        kind: "drill",
        lessonId: entry.lessonId,
        drillId: entry.drillId,
        drill: entry.drill,
        phase: entry.phase,
      }
    : { kind: "intro" };
}

function nextView(
  current: LevelView,
  level: Level,
  flatDrills: readonly FlatDrillEntry[],
): LevelView {
  if (current.kind === "intro") {
    const first = flatDrills[0];
    return first
      ? {
          kind: "drill",
          lessonId: first.lessonId,
          drillId: first.drillId,
          drill: first.drill,
          phase: first.phase,
        }
      : examView(level, 0);
  }
  if (current.kind === "drill") {
    const index = flatDrills.findIndex((candidate) => candidate.drillId === current.drillId);
    const next = flatDrills[index + 1];
    return next
      ? {
          kind: "drill",
          lessonId: next.lessonId,
          drillId: next.drillId,
          drill: next.drill,
          phase: next.phase,
        }
      : examView(level, 0);
  }
  if (current.kind === "exam") {
    return current.index + 1 < level.exam.drills.length
      ? examView(level, current.index + 1)
      : { kind: "stamp" };
  }
  if (current.kind === "stamp") {
    return { kind: "outro" };
  }
  return current;
}

export interface UseLevelFlowResult {
  readonly view: LevelView;
  readonly advance: () => void;
  readonly completeDrill: () => void;
  readonly completeExam: () => void;
}

/**
 * 段（S-2）内のビュー遷移を一本化する状態機械。V-1〜V-5の遷移は一方向（N-003）。
 * 出題順は記憶定着スケジューラ（習得ブロック→インターリーブ想起）に従う（F-011）。
 */
export function useLevelFlow(level: Level, onLevelComplete: () => void): UseLevelFlowResult {
  const progress = useProgressStore((state) => state.progress);
  const setProgress = useProgressStore((state) => state.setProgress);
  const skillMemory = useSkillMemoryStore((state) => state.memory);
  const setSkillMemory = useSkillMemoryStore((state) => state.setMemory);
  const flatDrills = useMemo(() => buildFlatDrillList(level), [level]);

  const [view, setView] = useState<LevelView>(() =>
    toInitialView(level, deriveResumeView(level, progress), flatDrills),
  );

  const repositoryRef = useRef<ProgressRepository | null>(null);
  if (repositoryRef.current === null) {
    repositoryRef.current = createProgressRepository();
  }
  const skillMemoryRepositoryRef = useRef<SkillMemoryRepository | null>(null);
  if (skillMemoryRepositoryRef.current === null) {
    skillMemoryRepositoryRef.current = createSkillMemoryRepository();
  }

  const advance = useCallback(() => {
    if (view.kind === "outro") {
      onLevelComplete();
      return;
    }
    setView((current) => nextView(current, level, flatDrills));
  }, [view, level, flatDrills, onLevelComplete]);

  const persistAndAdvance = useCallback(
    (nextProgress: Progress) => {
      repositoryRef.current?.save(nextProgress);
      setProgress(nextProgress);
      setView((current) => nextView(current, level, flatDrills));
    },
    [setProgress, level, flatDrills],
  );

  const recordSkillPractice = useCallback(
    (drill: Drill) => {
      const key = skillKeyOf(drill);
      if (key === null) {
        return;
      }
      const nextMemory = withSkillPractice(skillMemory, key);
      skillMemoryRepositoryRef.current?.save(nextMemory);
      setSkillMemory(nextMemory);
    },
    [skillMemory, setSkillMemory],
  );

  const completeDrill = useCallback(() => {
    if (view.kind !== "drill") {
      return;
    }
    recordSkillPractice(view.drill);
    persistAndAdvance(withClearedDrill(progress, `${level.id}:${view.drillId}`));
  }, [view, progress, level.id, persistAndAdvance, recordSkillPractice]);

  const completeExam = useCallback(() => {
    const isLastExamDrill = view.kind === "exam" && view.index + 1 >= level.exam.drills.length;
    if (view.kind === "exam") {
      recordSkillPractice(view.drill);
    }
    if (!isLastExamDrill) {
      setView((current) => nextView(current, level, flatDrills));
      return;
    }
    persistAndAdvance(withStamp(progress, level.id));
  }, [view, progress, level, flatDrills, persistAndAdvance, recordSkillPractice]);

  return { view, advance, completeDrill, completeExam };
}
